'use client';

import Image from 'next/image';
import React, { useCallback, useMemo, useRef, useState } from 'react';

import { TMDB_IMAGE_RESOLUTION } from '@cineamo/api/constants/tmdb-constants';

import { useFadingEdge } from '@cineamo/ui/src/hooks/useFadingEdge';

import Badge from '@cineamo/ui/src/badge/Badge';
import BackAndForthButtonOverlay from '@cineamo/ui/src/button/BackAndForthButtonOverlay';
import ImageGallery from '@cineamo/ui/src/image/ImageGallery';

/**
 * Pure presentational component for displaying a horizontal scrollable image gallery.
 * Shows images with badge count and opens lightbox gallery on click.
 * Supports both TMDB-prefixed URLs and direct image URLs.
 */
const MovieImageGallery: React.FC<{
    className?: string;
    images: string[];
    label?: React.ReactNode;
    imageHeight?: number;
    showImagesCountBadge?: boolean;
    useTmdbPrefix?: boolean;
    /** Called when the lightbox gallery is opened (via a thumbnail or the count badge). Receives the opened image index. */
    onGalleryOpen?: (index: number) => void;
}> = ({
    className,
    images,
    label,
    imageHeight = 164,
    useTmdbPrefix = true,
    showImagesCountBadge = false,
    onGalleryOpen
}) => {
    const imagesScrollContainerRef = useRef<HTMLDivElement | null>(null);
    const { ref: fadingEdgeRef } = useFadingEdge({ orientation: 'horizontal' });
    // BackAndForthButtonOverlay needs a RefObject, useFadingEdge a callback ref — attach both.
    const setScrollContainerRef = useCallback(
        (node: HTMLDivElement | null) => {
            imagesScrollContainerRef.current = node;
            fadingEdgeRef(node);
        },
        [fadingEdgeRef]
    );

    const [imageGalleryImageIndex, setImageGalleryImageIndex] = useState<number>();

    const getImageUrl = (image: string, resolution: string) => {
        return useTmdbPrefix ? resolution + image : image;
    };

    const uniqueImages = useMemo(() => {
        return [...new Set(images)];
    }, [images]);

    const openGallery = (index: number) => {
        setImageGalleryImageIndex(index);
        onGalleryOpen?.(index);
    };

    return (
        <>
            {imageGalleryImageIndex !== undefined && (
                <ImageGallery
                    currentIndex={imageGalleryImageIndex}
                    open={true}
                    onClose={() => setImageGalleryImageIndex(undefined)}
                    images={uniqueImages.map((image) => getImageUrl(image, TMDB_IMAGE_RESOLUTION.ORIGINAL))}
                />
            )}
            <div className={className}>
                <div className="w-full flex flex-row items-center space-x-16 justify-between lg:justify-start mb-12">
                    {label}
                    {showImagesCountBadge && (
                        <Badge
                            className="!text-body-bold-14 !px-16 !py-4 !rounded-4"
                            hoverable
                            onBadgeClick={() => uniqueImages.length > 0 && openGallery(0)}
                            name={uniqueImages.length.toString()}
                        />
                    )}
                </div>
                <div className="relative w-full">
                    <div
                        className="flex overflow-x-auto space-x-8 no-scrollbar"
                        ref={setScrollContainerRef}>
                        {uniqueImages?.map((image, index) => (
                            <div
                                key={`MovieImageGalleryItem#${image}`}
                                className="flex-none relative rounded-4 overflow-hidden cursor-pointer clickable-element"
                                onClick={() => openGallery(index)}>
                                <Image
                                    className="w-auto scale-on-hover object-cover"
                                    style={{ height: `${imageHeight}px` }}
                                    src={getImageUrl(image, TMDB_IMAGE_RESOLUTION.W780)}
                                    placeholder="blur"
                                    blurDataURL={getImageUrl(image, TMDB_IMAGE_RESOLUTION.W92)}
                                    width={300}
                                    height={200}
                                    title={`Gallery Image ${index + 1}`}
                                    alt={`Gallery Image ${index + 1}`}
                                />
                            </div>
                        ))}
                    </div>
                    <BackAndForthButtonOverlay
                        scrollContainerRef={imagesScrollContainerRef}
                        renderOutside
                    />
                </div>
            </div>
        </>
    );
};

export default MovieImageGallery;
